import React from 'react';
import classnames from 'classnames';
import { ICoreContext, Image, IImageSettings, ImageFetchPriority } from '@msdyn365-commerce/core';
import { ensureBaseImageUrl } from '../../shared-utils/image-utilities';
import { ProductListProduct } from '../product-list-component/product-list-component';
import { SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { getConversionText, getKilogramConversion, getLiterPrice, getNonBreakoffUOM, getPcsConversion, getRoundedFactor, getTotalConversionText } from '../../shared-utils/unit-conversion-utils';
import PriceComponent from '../../themes/dlvry-adventureworks/views/components/price.component';
import { multiplyPriceBy } from '../../shared-utils/price-utils';
import { humanizeUnitOfMeasure } from '../unit-of-measure';
import { getProductPageUrlSync, IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { IAlternativeProductsResources } from './alternative-products';
import { Availability } from './availability';
import QuantityInput from '../common/quantity-input';
import QuantityBreakoffSelector from '../common/quantity-breakoff-selector';
import { getHideConversionInCategories } from '../../settings/app.settings.utils';
import { cssCapitalize, isProductInCategory } from '../../shared-utils/common-utils';

type AlternativeProductLineProps = {
    id: string;
    typeName: string;
    context: ICoreContext;
    mode: 'edit' | 'view';
    resources: IAlternativeProductsResources;
    product: ProductListProduct;
    simpleProduct?: SimpleProduct;
    unitConversions?: UnitOfMeasureConversion[];
    availability?: IProductInventoryInformation;
    onChangeProduct?: (product: ProductListProduct, quantity: number, breakoff: boolean) => Promise<boolean>;
};

function ProductImage({
    context,
    product
}: {
    context: ICoreContext;
    product: ProductListProduct;
}) {
    const width = 90;
    const height = 90;
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
            lg: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
            xl: { w: width, h: height, q: `w=${width}px&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` }
        },
        disableLazyLoad: false,
        lazyload: true,
        quality: 80
    };

    const image = ensureBaseImageUrl(context, product.PrimaryImageUrl);
    const contextConfig = context.request.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = context?.request.apiSettings.baseImageUrl;
    if (fallbackImageSource?.endsWith('/')) {
        fallbackImageSource = fallbackImageSource.slice(0, -1);
    }
    if (emptyPlaceHolderImage) {
        fallbackImageSource = `${fallbackImageSource}${emptyPlaceHolderImage}`;
    }

    return (
        <Image
            requestContext={context.actionContext.requestContext}
            className='product-image'
            altText={product.Name}
            title={product.Name}
            src={image || fallbackImageSource}
            fallBackSrc={fallbackImageSource}
            gridSettings={context.request.gridSettings!}
            imageSettings={defaultImageSettings}
            loadFailureBehavior='empty'
            fetchPriority={ImageFetchPriority.High}
            tabIndex={0}
        />
    );
}

export function AlternativeProductLine({
    id,
    typeName,
    context,
    mode,
    resources,
    product,
    simpleProduct,
    unitConversions,
    availability,
    onChangeProduct
}: AlternativeProductLineProps) {
    const className = 'dlvry-alternative-products-popup';

    const [quantity, setQuantity] = React.useState(0);
    const [breakoff, setBreakoff] = React.useState(false);
    const [loadingAction, setLoadingAction] = React.useState(false);

    const conversions = unitConversions?.filter(x => x.ProductRecordId === product.RecordId);
    // Check if we should kg price for this product based on category settings
    const hideConversionInCategories = getHideConversionInCategories(context);
    const dontShowKgPrice = isProductInCategory(product, hideConversionInCategories);
    const kilogramConversion = !dontShowKgPrice && conversions && getKilogramConversion(simpleProduct?.DefaultUnitOfMeasure, conversions);
    const pcsConversion = conversions && getPcsConversion(conversions, simpleProduct?.DefaultUnitOfMeasure);

    let productPrice = product.ProductPrice ? { ...product.ProductPrice } : undefined;

    if (productPrice && !breakoff && simpleProduct?.Behavior?.MultipleOfQuantity) {
        productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
    }

    const price = product.ProductPrice?.CustomerContextualPrice;
    const productUrl = product ? getProductPageUrlSync(product.Name ?? '', product.RecordId, context.actionContext, undefined) : '';

    const totalConversionText = getTotalConversionText(
        context,
        simpleProduct,
        conversions,
        quantity,
        breakoff,
        product
    );

    const additionalInformation = (product.AlcoholProduct && product.AlcoholProduct > 0 && product.ImporterGrouping) && (
        <div className={`${className}__details__additional-information importer-grouping`}>{product.ImporterGrouping}</div>
    ) || product.Brand && (
        <div className={`${className}__details__additional-information brand`}>{product.Brand}</div>
    ) || product.Manufacturer && (
        <div className={`${className}__details__additional-information manufacturer`}>{product.Manufacturer}</div>
    ) || product.Producer && (
        <div className={`${className}__details__additional-information producer`}>{product.Producer}</div>
    );

    const conversionText = getConversionText(
        context,
        simpleProduct?.DefaultUnitOfMeasure,
        conversions || [],
        simpleProduct,
        breakoff,
        product
    );

    return (
        <li key={product.RecordId} className={`${className}__list__item`} role='listitem'>
            <div className={`${className}__image`} style={{ minWidth: 50, minHeight: 50 }}>
                <a href={productUrl} title={product.Name}>
                    <ProductImage context={context} product={product} />
                </a>
            </div>
            <div className={`${className}__details`}>
                <div className={`${className}__details__name-and-price`}>
                    <div className={`${className}__details__name-wrapper`}>
                        <a className={`${className}__details__name`} href={productUrl}>
                            {cssCapitalize(product.Name, context.request.locale)}
                        </a>

                        <div className={`${className}__details__id`}>
                            {product.ItemId}
                        </div>

                        {conversionText && (
                            <div className={`${className}__details__conversion-text`}>
                                {conversionText}
                            </div>
                        )}

                        {additionalInformation}
                    </div>

                    <div className={`${className}__price`}>
                        {productPrice && (
                            <PriceComponent
                                context={context}
                                id={id}
                                typeName={typeName}
                                data={{ price: productPrice }}
                            />
                        )}

                        {price && (!!(!breakoff && simpleProduct?.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1)) && (
                            <div className={`${className}__price__extra multiple`}>
                                <PriceComponent
                                    context={context}
                                    data={{ price: { CustomerContextualPrice: price } }}
                                    id={id}
                                    typeName={typeName} />
                                &nbsp;pr. {humanizeUnitOfMeasure(context, simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure).toLowerCase()}
                            </div>
                        )}

                        {price && pcsConversion && (
                            <div className={`${className}__price__extra pcs`}>
                                <PriceComponent
                                        context={context}
                                        data={{ price: { CustomerContextualPrice: price / (pcsConversion.Factor || 1) } }}
                                        id={id}
                                        typeName={typeName} />
                                &nbsp;pr. stk
                            </div>
                        )}

                        {price && kilogramConversion && getRoundedFactor(kilogramConversion) > 0 && (
                            <div className={`${className}__price__extra kilogram`}>
                                <PriceComponent
                                    context={context}
                                    data={{ price: { CustomerContextualPrice: price / getRoundedFactor(kilogramConversion) } }}
                                    id={id}
                                    typeName={typeName} />
                                &nbsp;pr. kg
                            </div>
                        )}

                        {price && conversions && getLiterPrice(conversions, simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure, price) && (
                            <div className={`${className}__price__extra liter`}>
                                <PriceComponent
                                    context={context}
                                    data={{ price: { CustomerContextualPrice: getLiterPrice(conversions, simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure, price) || 0 } }}
                                    id={id}
                                    typeName={typeName} />
                                &nbsp;pr. liter
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${className}__details__conversion`}>
                    {/* <div>
                        {!!isM1ProductAndHasBreakoff && `1 x ${simpleProduct!.Behavior!.MultipleOfQuantity} `}
                        {uom}
                    </div> */}
                    <QuantityBreakoffSelector
                        context={context}
                        product={simpleProduct}
                        quantity={quantity}
                        breakoff={breakoff}
                        unitConversions={conversions}
                        onBreakoffChange={setBreakoff}
                        onQuantityChange={setQuantity}
                    />
                    <QuantityInput
                        mode={mode}
                        context={context}
                        product={simpleProduct}
                        quantity={quantity}
                        breakoff={breakoff}
                        nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, conversions)}
                        onQuantityChange={setQuantity}
                    />
                    <span className={`${className}__total-conversion-text`} style={{ minWidth: 8.22 }}>
                        {totalConversionText && '='}
                    </span>
                    <span className={`${className}__total-conversion-text`} style={{ minWidth: 60 }}>
                        {totalConversionText}
                    </span>
                </div>
                <div className={`${className}__details__purchase-info`}>
                    <Availability baseClassName={className} context={context} availability={availability} />

                    <div className={`${className}__details__purchase-info__actions`}>
                        <button
                            className={classnames(`${className}__details__purchase-info__actions__change`, { loading: loadingAction })}
                            onClick={async () => {
                                setLoadingAction(true);
                                const result = await onChangeProduct?.(product, quantity, breakoff);
                                setLoadingAction(false);

                                if (result) {
                                    setQuantity(0);
                                }
                            }}
                        >
                            {resources.alternativeProductsChangeProductButtonText}
                        </button>
                    </div>
                </div>
            </div>
        </li>
    );
}
